import { ChangeEvent, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipLoader } from 'react-spinners';

import {
  getSmartParentProgramVideoPreview,
  getSmartParentsImage,
  getSmartParentsReferralPriceImage,
  getTextToGameVideoPreview,
  Images,
} from '../../../../constants/images';
import { SVGIconType } from '../../../../constants/svgIcon';
import {
  getCreateGameVideo,
  getGuaranteedLearningVideo,
} from '../../../../constants/videos';
import {
  AlbumCell,
  AlbumCellProps,
  ChangeCountry,
  LanguageRow,
  SvgIcon,
  TextButton,
  TextButtonFontSize,
  TextButtonSize,
  TextButtonVariant,
  TextInput,
  VideoPlayer,
} from '../../../Molecules';

import './SmartParentsProgramTemplate.css';

type TextInputProps = {
  value: string;
  disabled: boolean;
  errorMessage: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onBlur: () => void;
};

export type SmartParentsProgramTemplateProps = {
  originalYearCurrency: string;
  originalMonthCurrency: string;
  referralYearCurrency: string | undefined;
  referralMonthCurrency: string | undefined;
  tier1Currency: string | undefined;
  tier2Currency: string | undefined;
  referralCode: TextInputProps;
  validationLoading: boolean;
  purchasedPlan: boolean;
  albumCellProps: AlbumCellProps | undefined;
  onClickViewDetails: () => void;
  onClickPurchase: () => void;
  onClickViewReward: () => void;
  onClickViewGurantee: () => void;
  currency: string;
  isValid: boolean;
};

export const SmartParentsProgramTemplate = memo(
  ({
    originalYearCurrency,
    originalMonthCurrency,
    referralYearCurrency,
    referralMonthCurrency,
    tier1Currency,
    tier2Currency,
    referralCode,
    validationLoading,
    purchasedPlan,
    albumCellProps,
    // onClickViewDetails,
    onClickPurchase,
    onClickViewReward,
    onClickViewGurantee,
    currency,
    isValid,
  }: SmartParentsProgramTemplateProps) => {
    const { t, i18n } = useTranslation();

    return (
      <div className="smart-parents-program">
        <div className="centered-container">
          <div className="language-block">
            {/* <div className="text-green-l">{t('Not your language?')}</div> */}
            <LanguageRow />
            <ChangeCountry />
          </div>
        </div>
        <div className="centered-container">
          <div className="smart-title">
            <p className="text-white-l-bold">{t('Smart Parents Program')}</p>
          </div>
          <p className="text-green-l">
            {t('AI Revision Solution, 100% Results Guaranteed')}
          </p>
          <div className="smart-video">
            <VideoPlayer
              url={getGuaranteedLearningVideo(i18n.language)}
              thumbnailUrl={getSmartParentProgramVideoPreview(i18n.language)}
              playing={true}
              playIcon={<></>}
            />
          </div>
        </div>
        <div className="centered-container">
          <p className="text-green-l">
            {t('Three-in-one: Recitation, Dictation, Exams')}
          </p>
          <p className="text-black-m">
            {t(
              'Convert text into AI Memory Games, combined with AI-scheduled reviews, to boost memory effectiveness by up to 9 times! Our experiments shows 98% of users achieved significant results.',
            )}
          </p>
          <img
            src={getSmartParentsImage('img1', i18n.language)}
            className="img1"
            alt="JOYAJOY Banner"
          />
        </div>
        <div className="centered-container">
          <p className="text-green-l">
            {t('Earn JOY Cash by Referring New Users!')}
          </p>
          <p className="text-black-m">
            {t(
              'Easily earn enough to cover your program admission fee, or even more!',
            )}
          </p>
          {/* <img
            src={getSmartParentsImage('img2', i18n.language)}  // replaced with text
            className="img2"
            alt="JOYAJOY Banner"
          /> */}
          <div className="container-ref">
            <div className="title-ref">
              <div className="title-container-ref">
                <SvgIcon
                  name={SVGIconType.Referral}
                  className={'title-icon-ref'}
                  stroke={'#ffffff'}
                  fill={'#ffffff'}
                  color="#ffffff"
                />
                <p className="text-ref-title">{t('Double Referral Reward')}</p>
              </div>
            </div>
            <div className="container-icon-with-text-w">
              <SvgIcon
                name={SVGIconType.Person}
                className={'title-icon-person1'}
                stroke={'#ffffff'}
                // fill={'#4a6db0'}
                // color={'#4a6db0'}
              />
              <div className="container-title-with-text">
                <div className="text-blue1-spec">
                  {t('Tier 1 Referral Reward')}
                </div>
                <div className="text-price">{tier1Currency}</div>
              </div>
            </div>
            <div className="container-icon-with-text-w">
              <SvgIcon
                name={SVGIconType.Person}
                className={'title-icon-person2'}
                stroke={'#ffffff'}
                // fill={'#1e89c7'}
                // color={'#1e89c7'}
              />
              <div className="container-title-with-text">
                <div className="text-blue2-spec">
                  {t('Tier 2 Referral Reward')}
                </div>
                <div className="text-price">{tier2Currency}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="centered-container-nobg">
          <div className="container-blue">
            <p className="text-white-l-bold">{t('Smart Parents Program')}</p>
            <div className="container-white-round">
              <p className="text-black-m-bold">{t('Original Price:')}</p>
              <p className="text-grey-l-crossed">
                {t('{YEAR} / year', {
                  YEAR: originalYearCurrency,
                })}
              </p>
              <p className="text-grey-s">
                {t('(About {MONTH} per month)', {
                  MONTH: originalMonthCurrency,
                })}
              </p>
              {referralYearCurrency && referralMonthCurrency ? (
                <>
                  <hr />
                  <p className="text-black-m-bold">
                    {isValid ? t('Referral Price:') : t('Special Price:')}
                  </p>
                  <p className="text-cyan-l">
                    {t('{YEAR} / year', {
                      YEAR: referralYearCurrency,
                    })}
                  </p>
                  <p className="text-grey-s">
                    {t('(About {MONTH} per month)', {
                      MONTH: referralMonthCurrency,
                    })}
                  </p>
                </>
              ) : null}
            </div>
          </div>
          <div className="container-blue-light">
            <div className="smart-video">
              <VideoPlayer
                url={getCreateGameVideo(i18n.language)}
                thumbnailUrl={getTextToGameVideoPreview(i18n.language)}
                playing={true}
                playIcon={<></>}
              />
            </div>
            <div className="container-icon-with-text">
              <SvgIcon
                name={SVGIconType.BrainX9}
                className={'title-icon'}
                stroke={'#1ca798'}
                fill={'#1ca798'}
              />
              <div className="container-title-with-text">
                <p className="text-green-l">{t('Text to AI Memory Games')}</p>
                <p className="text-black-s">
                  {t(
                    'During the program period, you can convert 25,000 AI Memory Games per year.',
                  )}
                </p>
              </div>
            </div>
            <div className="container-icon-with-text">
              <SvgIcon
                name={SVGIconType.Conversion}
                className={'title-icon'}
                stroke={'#1ca798'}
                fill={'#1ca798'}
              />
              <div className="container-title-with-text">
                <p className="text-green-l">
                  {t('Value-added Service • Content Conversion')}
                </p>
                <p className="text-black-s">
                  {t(
                    "You'll receive support from WEA staff with 12 content conversions per year. Submit learning content not exceeding three A4 pages, and WEA staff will convert it into AI Memory Games within 7 days. 6 of the requests must be submitted during months 7-12, while the other 6 can be submitted anytime after program start.",
                  )}
                </p>
              </div>
            </div>
            <div className="container-icon-with-text">
              <SvgIcon
                name={SVGIconType.MemoryWithAI}
                className={'title-icon'}
                stroke={'#1ca798'}
                fill={'#1ca798'}
              />
              <div className="container-title-with-text">
                <p className="text-green-l">{t('AI Memory Boost')}</p>
                <p className="text-black-s">
                  {t(
                    'JOYAJOY AI Memory Boost feature provides AI-scheduled reviews, which boost memory retention by up to 9 times.',
                  )}
                </p>
              </div>
            </div>
            {/* <div className="learn-more">
              <TextButton
                text={t('Learn More')}
                size={TextButtonSize.Large}
                fontSize={TextButtonFontSize.Medium}
                variant={TextButtonVariant.Blue}
                onClick={onClickViewDetails}
              />
            </div> */}
          </div>
        </div>
        <div className="centered-container-yellow">
          <p className="text-green-l">
            {t('Buy now and refer JOYAJOY to the others!')}
          </p>
          <p className="text-black-s-bold">
            {t(
              referralCode.disabled
                ? 'You got a Referral Code to Get a Discount!'
                : purchasedPlan
                ? ''
                : 'Input a Referral Code to Get a Discount!',
            )}
          </p>
          <TextInput
            value={referralCode.value}
            onChange={referralCode.onChange}
            placeholder={t('Referral Code')}
            disabled={referralCode.disabled}
            errorMessage={referralCode.errorMessage}
            style={{
              backgroundColor: referralCode.disabled ? '#cccccc' : '#ffffff',
            }}
            onFocus={referralCode.onFocus}
            onBlur={referralCode.onBlur}
          />
          {validationLoading && (
            <div className="loading-spinner">
              <ClipLoader
                loading={validationLoading}
                size={20}
                color={'#34b9ae'}
              />
            </div>
          )}
          <TextButton
            text={t(purchasedPlan ? 'Purchased' : 'Purchase Now')}
            size={TextButtonSize.Large}
            fontSize={TextButtonFontSize.Medium}
            variant={
              purchasedPlan
                ? TextButtonVariant.Purchased
                : TextButtonVariant.Green
            }
            disabled={purchasedPlan || (referralCode.value !== '' && !isValid)}
            onClick={onClickPurchase}
          />
        </div>
        <div className="centered-container-nobg">
          <div className="container-purple">
            <SvgIcon
              name={SVGIconType.Referral}
              className={'title-icon'}
              stroke={'#ffffff'}
              fill={'#ffffff'}
              color={'#ffffff'}
            />
            <p className="text-ref-title">
              {t('Double Referral Reward Scheme')}
            </p>
          </div>
          <div className="container-purple-light">
            <img
              src={getSmartParentsReferralPriceImage(i18n.language, currency)}
              className="img3"
              alt="JOYAJOY Banner"
            />
            <div className="container-title-with-text">
              <p className="text-purple-blue-m">
                {t('Tier 1 Referral Reward')}
              </p>
              <p className="text-black-s">
                {t(
                  "For each participant you successfully refer, you'll receive {amount}.",
                  { amount: tier1Currency },
                )}
              </p>
            </div>
            <div className="container-title-with-text">
              <p className="text-blue-m">{t('Tier 2 Referral Reward')}</p>
              <p className="text-black-s">
                {t(
                  "For each new participant successfully referred by someone you referred, you'll receive {amount}.",
                  { amount: tier2Currency },
                )}
              </p>
            </div>
            <TextButton
              text={t('Learn More')}
              size={TextButtonSize.Large}
              fontSize={TextButtonFontSize.Medium}
              variant={TextButtonVariant.Purple}
              onClick={onClickViewReward}
            />
          </div>
        </div>
        <div className="centered-container-nobg">
          <div className="container-blue2">
            <SvgIcon
              name={SVGIconType.Gurantee}
              className={'title-icon'}
              stroke={'#ffffff'}
            />
            <p className="text-ref-title">
              {t('100% English Results Guaranteed')}
            </p>
            <p className="text-white-s">
              {t(
                'We guarantee a full refund if you are not satisfied with your improvement.',
              )}
            </p>
            <img
              src={getSmartParentsImage('img13', i18n.language)}
              className="img13"
              alt="JOYAJOY Banner"
            />
            <TextButton
              text={t('Learn More')}
              size={TextButtonSize.Large}
              fontSize={TextButtonFontSize.Medium}
              variant={TextButtonVariant.OrangeText}
              onClick={onClickViewGurantee}
            />
          </div>
        </div>
        <div className="centered-container-nobg">
          <div className="container-orange">
            <SvgIcon
              name={SVGIconType.Star}
              className={'title-icon'}
              stroke={'#ffffff'}
            />
            <p className="text-ref-title">
              {t('TV Interview and Competition Opportunities')}
            </p>
            <img
              src={getSmartParentsImage('img4', i18n.language)}
              className="img4"
              alt="JOYAJOY Banner"
            />
            <p className="text-white-s">
              {t(
                'Program participants may have the chance to be interviewed on TV and be invited to participate in the WEA World Cup English Competition.',
              )}
            </p>
            {/* <TextButton
              text={t('Learn More')}
              size={TextButtonSize.Large}
              fontSize={TextButtonFontSize.Medium}
              variant={TextButtonVariant.BlueText}
              onClick={onClickViewDetails}
            /> */}
          </div>
        </div>
        <div className="centered-container-nobg">
          <div className="container-green">
            <SvgIcon
              name={SVGIconType.BookGame}
              className={'title-icon'}
              stroke={'#ffffff'}
            />
            <p className="text-ref-title">{t('AI Gamified Learning')}</p>
          </div>
          <div className="container-green-light">
            <SvgIcon
              name={SVGIconType.BrainX9}
              className={'title-icon'}
              stroke={'#1ca798'}
              fill={'#1ca798'}
            />
            <p className="text-green-l">{t('AI Memory Game')}</p>
            <div className="container-red">
              <p className="text-white-s">{t("World's 1st")}</p>
            </div>
            <p className="text-cyan-m">
              {t('Reduce Three Hours of Revision to About 1 Hour')}
            </p>
            <p className="text-green-m">
              {t(
                'According to experiments, JOYAJOY AI Memory Games can speed up learning by approximately 3 times.',
              )}
            </p>
            <p className="text-green-m">
              {t(
                'Convert the content of your textbooks, worksheets, dictations, recitations, quizzes, and exams into AI Memory Games to achieve rapid learning.',
              )}
            </p>
            <p className="text-black-xs">
              {t(
                'Provisional Patent: No. 63669762 (25 Items) • Copyrights: No. 1-14003935451 (12 Items)',
              )}
            </p>
            <div className="container-white-round">
              <div className="container-icon-with-text2">
                <SvgIcon
                  name={SVGIconType.QuickMemoryJoy}
                  className={'title-icon'}
                  stroke={'#1ca798'}
                />
                <p className="text-ref-title-green">{t('Quick Memory JOY')}</p>
              </div>
              <img
                src={getSmartParentsImage('img5', i18n.language)}
                className="img5"
                alt="JOYAJOY Banner"
              />
            </div>
            <div className="container-white-round">
              <div className="container-icon-with-text2">
                <SvgIcon
                  name={SVGIconType.SpellingJoy}
                  className={'title-icon'}
                  stroke={'#1ca798'}
                />
                <p className="text-ref-title-green">{t('Spelling JOY')}</p>
              </div>
              <img
                src={getSmartParentsImage('img6', i18n.language)}
                className="img6"
                alt="JOYAJOY Banner"
              />
            </div>
            <img
              src={getSmartParentsImage('img14', i18n.language)}
              className="img14"
              alt="JOYAJOY Banner"
            />
            <p className="text-black-s">
              {t(
                'Convert the content of your textbooks, worksheets, dictations, recitations, quizzes, and exams into AI Memory Games to achieve rapid learning.',
              )}
            </p>
            {albumCellProps ? (
              <div className="try-cell-container">
                <div className="title-all-component">
                  <div className="recent-text">{t('Try AI Memory Games')}</div>
                  <div
                    className="all-component"
                    onClick={albumCellProps.onClick}
                  >
                    <div className="all-text">{t('All')}</div>
                    <SvgIcon name={SVGIconType.Arrow} className="arrow-icon" />
                  </div>
                </div>
                <div className="try-cell-view">
                  <AlbumCell
                    album={albumCellProps.album}
                    albumClassroom={albumCellProps.albumClassroom}
                    classroomOwnerProfile={albumCellProps.classroomOwnerProfile}
                    onClick={albumCellProps.onClick}
                  />
                </div>
              </div>
            ) : null}
            {/* <TextButton
              text={t('Learn More')}
              size={TextButtonSize.Large}
              fontSize={TextButtonFontSize.Medium}
              variant={TextButtonVariant.Green}
              onClick={onClickViewDetails}
            /> */}
          </div>
        </div>
        <div className="centered-container-nobg">
          <div className="container-green-light">
            <SvgIcon
              name={SVGIconType.MemoryWithAI}
              className={'title-icon'}
              stroke={'#1ca798'}
              fill={'#1ca798'}
              color={'#1ca798'}
            />
            <p className="text-green-l">{t('AI Memory Boost')}</p>
            <img
              src={getSmartParentsImage('img7', i18n.language)}
              className="img7"
              alt="JOYAJOY Banner"
            />
            <p className="text-black-s">
              {t(
                'JOYAJOY AI Memory Boost provides AI-scheduled reviews, boosting memory retention by up to 9 times.',
              )}
            </p>
            <img
              src={getSmartParentsImage('img8', i18n.language)}
              className="img8"
              alt="JOYAJOY Banner"
            />
            <p className="text-black-s">
              {t(
                "According to Hermann Ebbinghaus's 1885 study, only about 10% of knowledge is retained by the seventh day after learning.",
              )}
            </p>
          </div>
        </div>
        <div className="centered-container-nobg">
          <div className="container-green-light">
            <p className="text-green-l">
              {t('Analysis · Translation · Speech')}
            </p>
            <img
              src={getSmartParentsImage('img9', i18n.language)}
              className="img9"
              alt="JOYAJOY Banner"
            />
            <div className="container-icon-with-text">
              <SvgIcon
                name={SVGIconType.ScoreAnalysis}
                className={'title-icon2'}
                stroke={'#1ca798'}
                fill={'#1ca798'}
                color={'#1ca798'}
              />
              <p className="text-green-m-bold">
                {t('Analyze your game results.')}
              </p>
            </div>
            <div className="container-icon-with-text">
              <SvgIcon
                name={SVGIconType.Translation}
                className={'title-icon2'}
                stroke={'#1ca798'}
                fill={'#1ca798'}
                color={'#1ca798'}
              />
              <p className="text-green-m-bold">
                {t('Translate the game content.')}
              </p>
            </div>
            <div className="container-icon-with-text">
              <SvgIcon
                name={SVGIconType.TTS}
                className={'title-icon2'}
                stroke={'#1ca798'}
                fill={'#1ca798'}
                color={'#1ca798'}
              />
              <p className="text-green-m-bold">
                {t('Listen to the game content to improve your pronunciation.')}
              </p>
            </div>
          </div>
        </div>
        <div className="centered-container">
          <p className="text-green-m">{t('Awards & Recognition')}</p>
          <img
            src={getSmartParentsImage('img10', i18n.language)}
            className="img10"
            alt="JOYAJOY Banner"
          />
          <p className="text-green-l">{t('The Best Learning App')}</p>
          <p className="text-black-s">
            {t(
              'PCCW presented The Best Learning App Award to JOYAJOY (formerly DipDip) for the Yellow Pages Award.',
            )}
          </p>

          <img
            src={getSmartParentsImage('img11', i18n.language)}
            className="img11"
            alt="JOYAJOY Banner"
          />
          <p className="text-green-l">{t('HKCT Business Award')}</p>
          <p className="text-black-s">
            {t(
              'Under Secretary for Commerce and Econ Dev, HK Govt, Dr. Bernard Chan presented the HKCT Business Award to JOYAJOY.',
            )}
          </p>
          <img src={Images.AnnieLaw} className="img11" alt="Annie Law" />
          <p className="text-green-l">{t('Professor Annie Law, Psychology')}</p>
          <p className="text-black-s">
            {t(
              `"Through AI intelligent learning, students' memory abilities can be enhanced and learning obstacles can be solved."`,
            )}
          </p>
        </div>
        <div className="centered-container">
          <img
            src={getSmartParentsImage('img12', i18n.language)}
            className="img12"
            alt="JOYAJOY Banner"
          />
          <div className="specific-container">
            <div className="container-icon-with-text">
              <SvgIcon
                name={SVGIconType.Improve}
                className={'title-icon'}
                stroke={'#1ca798'}
                fill={'#1ca798'}
                color={'#1ca798'}
              />
              <p className="text-green-l">
                {t('Enhance Learning Effectiveness')}
              </p>
            </div>
            <div className="container-icon-with-text">
              <SvgIcon
                name={SVGIconType.Family}
                className={'title-icon'}
                stroke={'#1ca798'}
                fill={'#1ca798'}
                color={'#1ca798'}
              />
              <p className="text-green-l">
                {t('Reduce Parent-Child Conflicts')}
              </p>
            </div>
            <div className="container-icon-with-text">
              <SvgIcon
                name={SVGIconType.Cash}
                className={'title-icon'}
                stroke={'#1ca798'}
                fill={'#1ca798'}
                color={'#1ca798'}
              />
              <p className="text-green-l">{t('Increase Passive Income')}</p>
            </div>
            <div className="container-icon-with-text">
              <SvgIcon
                name={SVGIconType.Happy}
                className={'title-icon'}
                stroke={'#1ca798'}
                fill={'#1ca798'}
                color={'#1ca798'}
              />
              <p className="text-green-l">{t('Alleviate Life Pressures')}</p>
            </div>
          </div>
        </div>
        <div className="centered-container-nobg">
          <div className="container-orange">
            <p className="text-ref-title">{t('Smart Parents Program')}</p>
            <div className="container-white-round">
              <p className="text-black-m-bold">{t('Original Price:')}</p>
              <p className="text-grey-l-crossed">
                {t('{YEAR} / year', {
                  YEAR: originalYearCurrency,
                })}
              </p>
              <p className="text-grey-s">
                {t('(About {MONTH} per month)', {
                  MONTH: originalMonthCurrency,
                })}
              </p>
              {referralYearCurrency && referralMonthCurrency ? (
                <>
                  <hr />
                  <p className="text-black-m-bold">
                    {isValid ? t('Referral Price:') : t('Special Price:')}
                  </p>
                  <p className="text-cyan-l">
                    {t('{YEAR} / year', {
                      YEAR: referralYearCurrency,
                    })}
                  </p>
                  <p className="text-grey-s">
                    {t('(About {MONTH} per month)', {
                      MONTH: referralMonthCurrency,
                    })}
                  </p>
                </>
              ) : null}
            </div>
          </div>
          <div className="centered-container-yellow">
            <p className="text-green-l">
              {t('Buy now and refer JOYAJOY to the others!')}
            </p>
            <p className="text-black-s-bold">
              {t(
                referralCode.disabled
                  ? 'You got a Referral Code to Get a Discount!'
                  : purchasedPlan
                  ? ''
                  : 'Input a Referral Code to Get a Discount!',
              )}
            </p>
            <TextInput
              value={referralCode.value}
              onChange={referralCode.onChange}
              placeholder={t('Referral Code')}
              disabled={referralCode.disabled}
              errorMessage={referralCode.errorMessage}
              style={{
                backgroundColor: referralCode.disabled ? '#cccccc' : '#ffffff',
              }}
              onFocus={referralCode.onFocus}
              onBlur={referralCode.onBlur}
            />
            {validationLoading && (
              <div className="loading-spinner">
                <ClipLoader
                  loading={validationLoading}
                  size={20}
                  color={'#34b9ae'}
                />
              </div>
            )}
            <TextButton
              text={t(purchasedPlan ? 'Purchased' : 'Purchase Now')}
              size={TextButtonSize.Large}
              fontSize={TextButtonFontSize.Medium}
              variant={
                purchasedPlan
                  ? TextButtonVariant.Purchased
                  : TextButtonVariant.Green
              }
              disabled={
                purchasedPlan || (referralCode.value !== '' && !isValid)
              }
              onClick={onClickPurchase}
            />
          </div>
        </div>
        {}
        <hr />
        {/* <div className="centered-container">
          <div className="title-container">
            <SvgIcon
              name={SVGIconType.BrainX9}
              className={'title-icon'}
              stroke={'#000'}
              fill={'#000'}
            />
            <div className="title-text">{t('AI Memory Games')}</div>
          </div>

          <div className="white-container">
            <div className="program-text">{t('Smart Parents Program')}</div>

            <div className="program-grey-container">
              <div className="program-price-container">
                <div className="program-description">
                  {t('Original Price:')}
                </div>
                <div className="program-price">
                  {t('{YEAR} / year', {
                    YEAR: originalYearCurrency,
                  })}
                </div>
                <div className="program-sub-price">
                  {t('(About {MONTH} per month)', {
                    MONTH: originalMonthCurrency,
                  })}
                </div>
              </div>
              {referralYearCurrency && referralMonthCurrency ? (
                <>
                  <hr />
                  <div className="program-price-container">
                    <div className="program-description">
                      {t('Referral Price:')}
                    </div>
                    <div className="program-price">
                      {t('{YEAR} / year', {
                        YEAR: referralYearCurrency,
                      })}
                    </div>
                    <div className="program-sub-price">
                      {t('(About {MONTH} per month)', {
                        MONTH: referralMonthCurrency,
                      })}
                    </div>
                  </div>
                </>
              ) : null}
            </div>
            <div className="logo-container">
              <img
                className="logo-icon"
                src={Icons[IconType.JOYAJOYxWEAColumn]}
              />
            </div>
            <div className="smart-video">
              <VideoPlayer url={GuaranteedLearningVideo} />
            </div>
            <div className="description-text">
              {t(
                'Learn with tailored AI Memory Games, boost memory up to 9 times with AI-scheduled revisions.',
              )}
            </div>
            <div className="description-text">
              {t('100% results guaranteed or your money back!')}
            </div>
            <hr className="divided-line" />
            <div className="detail-container">
              <img
                className="detail-icon"
                src={Icons[IconType.CustomizeGame]}
              />
              <div className="sub-container">
                <div className="sub-title">
                  {t('Make Learning Games Yourself')}
                </div>
                <div className="sub-text">
                  {t(
                    "The program comes with JOYAJOY's premium features, allowing you to easily create and play 25,000 Memory Games of any content you want.",
                  )}
                </div>
              </div>
            </div>
            <div className="detail-container">
              <img className="detail-icon" src={Icons[IconType.Conversation]} />
              <div className="sub-container">
                <div className="sub-title">
                  {t('Value-added Services · Content Conversion')}
                </div>
                <div className="sub-text">
                  {t(
                    'Customized AI Memory Games prepared by WEA’s Content Specialist Team just for you, according to text book, worksheet, handouts you submit.',
                  )}
                </div>
              </div>
            </div>
            <div className="detail-container">
              <img
                className="detail-icon"
                src={Icons[IconType.WebsiteReferral]}
              />
              <div className="sub-container">
                <div className="sub-title">
                  {t('Two-Tiered Referral Scheme')}
                </div>
                <div className="sub-text">
                  {t(
                    'Early participants can earn generous referral rewards. You might easily cover your admission fee or earn even more!',
                  )}
                </div>
              </div>
            </div>
            <div className="detail-container">
              <img className="detail-icon" src={Icons[IconType.Guarantee]} />
              <div className="sub-container">
                <div className="sub-title">{t('100% Results Guaranteed')}</div>
                <div className="sub-text">
                  {t("We're confident in our program's effectiveness.")}
                </div>
              </div>
            </div>
            <TextButton
              text={t('View Plan Details')}
              size={TextButtonSize.Large}
              fontSize={TextButtonFontSize.Medium}
              variant={TextButtonVariant.Transparent}
              onClick={onClickViewDetails}
            />
            <div className="code-title">
              {t(
                referralCode.disabled
                  ? 'You got a Referral Code to Get a Discount!'
                  : purchasedPlan
                  ? ''
                  : 'Input a Referral Code to Get a Discount!',
              )}
            </div>
            <div className="text-input-container">
              <TextInput
                value={referralCode.value}
                onChange={referralCode.onChange}
                placeholder={t('Referral Code')}
                disabled={referralCode.disabled}
                errorMessage={referralCode.errorMessage}
                style={{
                  backgroundColor: referralCode.disabled
                    ? '#cccccc'
                    : '#f2f2f2',
                }}
                onFocus={referralCode.onFocus}
                onBlur={referralCode.onBlur}
              />
              {validationLoading && (
                <div className="loading-spinner">
                  <ClipLoader
                    loading={validationLoading}
                    size={20}
                    color={'#34b9ae'}
                  />
                </div>
              )}
            </div>

            <div className="purchase-now-container">
              <TextButton
                text={t(purchasedPlan ? 'Purchased' : 'Purchase Now')}
                size={TextButtonSize.Large}
                fontSize={TextButtonFontSize.Medium}
                disabled={purchasedPlan}
                variant={
                  purchasedPlan
                    ? TextButtonVariant.Gray
                    : TextButtonVariant.Cyan
                }
                onClick={onClickPurchase}
              />
            </div>
          </div>
        </div> */}
      </div>
    );
  },
);
